'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string;
  details: string[];
}

export default function LocationAccessSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: 'Where is Proximus located?',
      answer: 'Proximus is strategically located in Bangalore with excellent connectivity to business districts, airport, and key landmarks.',
      details: [
        'Address: 139/1, 2A, 1, 1st Main Rd, Sarvabhoumanagar, Sundar Ram Shetty Nagar, Bilekahalli, Bengaluru, Karnataka 560076, India',
        '60-75 minutes from Kempegowda International Airport (45 km)',
        '160 m from IIM Bangalore South Gate',
        'Walking distance to restaurants, cafes, and shopping centers',
        'Easy access to public transportation and metro stations',
      ],
    },
    {
      question: 'How do I reach Proximus from the airport?',
      answer: 'Multiple transportation options available including airport taxis, app-based cabs, and our airport transfer service.',
      details: [
        'Airport taxi: ₹1000-₹1,200 (75-100 minutes depending on traffic)',
        'App-based cabs (Uber/Ola): ₹1100-₹1,300',
        'Proximus airport transfer service: ₹1,300 (advance booking required)',
        'Airport bus service available with stops nearby',
        'Detailed directions provided in booking confirmation',
      ],
    },
    {
      question: 'Is public transportation accessible?',
      answer: 'Yes, we are well-connected to public transportation with metro, bus stops, and auto-rickshaw stands nearby.',
      details: [
        'IIMB/Apollo Hospital Metro Station: 700 m',
        'Yelachenahalli Metro Station (Green Line): 3.4 km',
        'Bus stops: 300-500 m on Bannerghatta Road with frequent services',
        'Auto-rickshaw stands available at property entrance',
        'App-based cab services (Ola/Uber) readily available 24/7',
        'Bicycle rental services nearby for local exploration',
      ],
    },
    {
      question: 'What are the nearby attractions and amenities?',
      answer: 'Surrounded by dining, shopping, entertainment, and business facilities within walking or short driving distance.',
      details: [
        'Restaurants & cafes: 50+ options within 1km radius',
        'Shopping malls: 2 major malls within 3km',
        'Supermarkets & grocery stores: 500 meters',
        'Hospitals & pharmacies: 24/7 facilities within 2km',
        'Banks & ATMs: Multiple options within 500 meters',
        'Gyms & fitness centers: 3 options within 1km',
      ],
    },
    {
      question: 'Is the property accessible for guests with disabilities?',
      answer: 'Yes, we provide wheelchair-accessible facilities and accommodations for guests with special needs.',
      details: [
        'Wheelchair-assistance at entrance and common areas',
        'Elevator access to all floors',
        'Accessible parking spaces near entrance',
        'Ground floor apartments available on request',
        'Bathroom grab bars and accessible fixtures in select units',
        'Staff trained to assist guests with special needs',
      ],
    },
    {
      question: 'What corporate hubs are nearby?',
      answer: 'Proximus is ideally located near major IT parks, business districts, and corporate offices.',
      details: [
        'IIM Bangalore: 160 m (1 min drive)',
        'SJR Primeco Tech Park: 850 m (4 min drive)',
        'Koramangala Business District: 7 km (25 min drive)',
        'Electronic City: 17 km (25 min drive)',
        'HSBC HDPI: 200 m (2 min drive)',
        'Honeywell, Bosch and other MNCs: within 1.5 km',
      ],
    },
  ];

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="location-access" className="py-16 lg:py-24 bg-background">
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 text-accent mb-4">
            <Icon name="MapPinIcon" size={24} />
            <span className="text-sm font-semibold uppercase tracking-wide">Location & Access</span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-primary mb-4">
            Location Information
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Everything about our location, transportation options, nearby amenities, and accessibility features.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-subtle border border-border overflow-hidden hover:shadow-elevated transition-all duration-300"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted transition-colors duration-300"
              >
                <div className="flex-1 pr-4">
                  <h3 className="font-display text-xl font-semibold text-primary mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-text-secondary text-sm">{faq.answer}</p>
                </div>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  className="text-text-secondary flex-shrink-0"
                />
              </button>

              {openIndex === index && (
                <div className="px-6 pb-6 animate-slide-in-left">
                  <div className="border-t border-border pt-4">
                    <ul className="space-y-3">
                      {faq.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon
                            name="CheckCircleIcon"
                            size={20}
                            className="text-accent flex-shrink-0 mt-0.5"
                          />
                          <span className="text-text-secondary text-sm leading-relaxed">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white rounded-xl shadow-subtle border border-border p-8">
          <div className="text-center">
            <h3 className="font-display text-2xl font-bold text-primary mb-4">
              Still Have Questions?
            </h3>
            <p className="text-text-secondary mb-6">
              Our support team is available 24/7 to assist you with any inquiries.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact-us"
                className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all duration-300"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={20} />
                <span>Contact Support</span>
              </a>
              <a
                href="tel:+91XXXXXXXXXX"
                className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-accent hover:bg-accent/90 text-white font-semibold rounded-lg transition-all duration-300"
              >
                <Icon name="PhoneIcon" size={20} />
                <span>Call Us Now</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}